import { FluzErrorRest } from '@fluzfluzny/common-sdk/errors';
import { useAlert } from '~/components/notifications/useAlert';
import { AlertLevel } from '~/enums/Alert';
import { logError } from '~/utils/client-error-logger';

enum NotificationType {
  FORM = 'FORM',
  ALERT = 'ALERT',
}

interface NotificationDetails {
  level?: AlertLevel;
  type?: NotificationType;
  title?: string;
}

export interface FluzError {
  errorRecord: {
    code: string;
    name: string;
    message: string;
    status_code: number;
    user_friendly: boolean;
    notification_details: NotificationDetails;
  };
}

export interface ResponseError {
  success: boolean;
  statusCode: number;
  msg: string;
  error: FluzError | Record<string, unknown>;
}

export interface DeclineErrorPayload {
  internalErrorCode?: number;
  declineErrorCode?: string;
  title?: string;
  message?: string;
}

export function useErrorHandler() {
  const { addAlert } = useAlert();
  function showErrorAlert(title: string, message: string, level: AlertLevel = 'error') {
    addAlert({
      title,
      bodyText: message,
      state: level,
      closeOnClickOutside: true,
    });
  }

  const fallbackTitle = 'OOPS, SOMETHING WENT WRONG';
  const fallbackMsg =
    'Sorry, we ran into an issue while processing your request, please refresh the page and try again or contact customer support';

  function parseResponseError(responseError: ResponseError, canHandleFormError = false) {
    const statusCode = responseError && responseError.statusCode ? responseError.statusCode : null;
    const isFluzError = !!responseError.error?.errorRecord;
    let displayTitle = fallbackTitle;
    let displayMessage = responseError.msg ?? fallbackMsg;

    // Log 500+ errors
    if (statusCode && statusCode >= 500) {
      logError(`API Error: ${statusCode}`, {
        statusCode,
        msg: responseError.msg,
        error: responseError.error,
        isFluzError,
      });
    }

    if (isFluzError) {
      const { errorRecord } = responseError.error as FluzError;
      const { message, notification_details } = errorRecord;
      const { level, type, title } = notification_details || {};
      displayTitle = title ?? fallbackTitle;
      displayMessage = message;
      if (type === 'FORM' && canHandleFormError) {
        return {
          type,
          message,
          level,
        };
      } else {
        showErrorAlert(displayTitle, message, level);
      }
    } else if (statusCode === 498) {
      return;
    } else {
      showErrorAlert(displayTitle, displayMessage);
    }
  }
  return { parseResponseError, showErrorAlert };
}

export function extractDeclineError(data: FluzErrorRest): DeclineErrorPayload {
  const errorRecord = data?.errorRecord;
  const { code, code_number, message, notification_details } = errorRecord || {};

  return {
    internalErrorCode: code_number,
    declineErrorCode: code,
    message: message,
    title: notification_details?.title,
  };
}
